/**
 * usePsychometricInterview
 *
 * Manages psychometric test answer state, navigation, and per-question submission.
 * Answers are persisted to localStorage (key: `psychometric_answers_{interviewId}`)
 * for page-refresh resilience.
 *
 * Supports multi-test flows (multiple psychometric sections in sequence).
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { PsychometricSectionSnapshot } from '../types/interview.types';
import { submitPsychometricAnswer, bulkSubmitPsychometricAnswers } from '../services/interview.service';

// answers keyed by test_slug -> question_id -> answer_value
type AnswerMap = Record<string, Record<number, number>>;

const LOCAL_STORAGE_KEY_PREFIX = 'psychometric_answers_';

function loadFromLocalStorage(interviewId: string): AnswerMap {
  try {
    const raw = localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}${interviewId}`);
    if (raw) {
      return JSON.parse(raw) as AnswerMap;
    }
  } catch {
    // ignore parse errors
  }
  return {};
}

function saveToLocalStorage(interviewId: string, answers: AnswerMap): void {
  try {
    localStorage.setItem(
      `${LOCAL_STORAGE_KEY_PREFIX}${interviewId}`,
      JSON.stringify(answers)
    );
  } catch {
    // ignore storage errors (quota exceeded etc.)
  }
}

export interface UsePsychometricInterviewResult {
  // Current state
  currentTestIndex: number;
  currentQuestionIndex: number;
  answers: AnswerMap;

  // Current items (derived)
  currentSection: PsychometricSectionSnapshot | null;
  currentQuestion: PsychometricSectionSnapshot['questions_data'][number] | null;

  // Navigation
  canGoNext: boolean;
  canGoPrevious: boolean;
  isLastQuestion: boolean;
  progress: number; // 0-100

  // Answer management
  setAnswer: (value: number) => void;
  currentAnswer: number | undefined;

  // Actions
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  completeTest: () => Promise<void>;

  // Completion
  isComplete: boolean;
  unansweredCount: number;
}

export function usePsychometricInterview(
  interviewId: string,
  psychometricSections: PsychometricSectionSnapshot[]
): UsePsychometricInterviewResult {
  const [answers, setAnswers] = useState<AnswerMap>(() =>
    loadFromLocalStorage(interviewId)
  );
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Track submitted answers to avoid redundant API calls
  const submittedRef = useRef<Set<string>>(new Set());

  // Per-question debounce timers so rapid answering doesn't cancel earlier submits
  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Persist answers whenever they change
  useEffect(() => {
    saveToLocalStorage(interviewId, answers);
  }, [interviewId, answers]);

  // Derived current items
  const currentSection = psychometricSections[currentTestIndex] ?? null;
  const currentQuestion = currentSection?.questions_data?.[currentQuestionIndex] ?? null;

  // Total question count across all tests
  const totalQuestions = useMemo(
    () => psychometricSections.reduce((sum, s) => sum + (s.questions_data?.length ?? 0), 0),
    [psychometricSections]
  );

  // Answered question count
  const answeredCount = useMemo(() => {
    return psychometricSections.reduce((sum, section) => {
      const sectionAnswers = answers[section.test_slug] ?? {};
      return sum + ((section.questions_data ?? []).filter((q) => sectionAnswers[q.id] !== undefined)).length;
    }, 0);
  }, [answers, psychometricSections]);

  const unansweredCount = totalQuestions - answeredCount;

  const isComplete = unansweredCount === 0;

  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // Navigation flags
  const isFirstQuestion = currentTestIndex === 0 && currentQuestionIndex === 0;
  const isLastTest = currentTestIndex === psychometricSections.length - 1;
  const isLastQuestionInTest =
    currentSection !== null &&
    currentQuestionIndex === ((currentSection.questions_data ?? []).length) - 1;
  const isLastQuestion = isLastTest && isLastQuestionInTest;

  const canGoNext = !isLastQuestion;
  const canGoPrevious = !isFirstQuestion;

  // Current answer value
  const currentAnswer =
    currentQuestion && currentSection
      ? answers[currentSection.test_slug]?.[currentQuestion.id]
      : undefined;

  // Set answer for current question
  const setAnswer = useCallback(
    (value: number) => {
      if (!currentSection || !currentQuestion) return;

      const testSlug = currentSection.test_slug;
      const questionId = currentQuestion.id;

      setAnswers((prev) => {
        const updated: AnswerMap = {
          ...prev,
          [testSlug]: {
            ...(prev[testSlug] ?? {}),
            [questionId]: value,
          },
        };
        return updated;
      });

      // Debounced submit — each answer gets its own timer so rapid answering
      // doesn't cancel earlier pending submits.
      const perQuestionKey = `${testSlug}_${questionId}`;
      if (debounceTimersRef.current[perQuestionKey]) {
        clearTimeout(debounceTimersRef.current[perQuestionKey]);
      }
      const submitKey = `${testSlug}_${questionId}_${value}`;
      debounceTimersRef.current[perQuestionKey] = setTimeout(async () => {
        delete debounceTimersRef.current[perQuestionKey];
        if (submittedRef.current.has(submitKey)) return;
        submittedRef.current.add(submitKey);
        const res = await submitPsychometricAnswer(interviewId, testSlug, questionId, value);
        if (res.error) {
          console.error('[usePsychometricInterview] Failed to submit answer:', res.error);
          submittedRef.current.delete(submitKey);
        }
      }, 250);
    },
    [currentSection, currentQuestion, interviewId]
  );

  // Submit current question's answer immediately (used on navigation)
  const submitCurrentAnswer = useCallback(async () => {
    if (!currentSection || !currentQuestion) return;

    const testSlug = currentSection.test_slug;
    const questionId = currentQuestion.id;
    const value = answers[testSlug]?.[questionId];

    if (value === undefined) return; // no answer to submit

    const submitKey = `${testSlug}_${questionId}_${value}`;
    if (submittedRef.current.has(submitKey)) return; // already submitted

    submittedRef.current.add(submitKey);
    const res = await submitPsychometricAnswer(interviewId, testSlug, questionId, value);
    if (res.error) {
      console.error('[usePsychometricInterview] Failed to submit answer on navigation:', res.error);
      toast.error(i18n.t('interview:toasts.psychometric_save_retry'));
      // Remove from submitted set so it can be retried
      submittedRef.current.delete(submitKey);
    }
  }, [currentSection, currentQuestion, answers, interviewId]);

  // Go to next question (fire-and-forget submit, navigate immediately)
  const goToNext = useCallback(async () => {
    if (!currentSection) return;

    // Clear pending debounce for current question
    if (currentQuestion) {
      const key = `${currentSection.test_slug}_${currentQuestion.id}`;
      if (debounceTimersRef.current[key]) {
        clearTimeout(debounceTimersRef.current[key]);
        delete debounceTimersRef.current[key];
      }
    }

    // Fire-and-forget submit — don't block navigation.
    // The bulk submit on completion will catch any missed answers.
    submitCurrentAnswer();

    if (isLastQuestion) return;

    if (isLastQuestionInTest && !isLastTest) {
      // Move to next test, first question
      setCurrentTestIndex((t) => t + 1);
      setCurrentQuestionIndex(0);
    } else {
      setCurrentQuestionIndex((q) => q + 1);
    }
  }, [currentSection, currentQuestion, isLastQuestion, isLastQuestionInTest, isLastTest, submitCurrentAnswer]);

  // Go to previous question
  const goToPrevious = useCallback(() => {
    if (isFirstQuestion) return;

    if (currentQuestionIndex === 0 && currentTestIndex > 0) {
      // Move to last question of previous test
      const prevSection = psychometricSections[currentTestIndex - 1];
      setCurrentTestIndex((t) => t - 1);
      setCurrentQuestionIndex(((prevSection.questions_data ?? []).length) - 1);
    } else {
      setCurrentQuestionIndex((q) => q - 1);
    }
  }, [isFirstQuestion, currentQuestionIndex, currentTestIndex, psychometricSections]);

  // Complete test: bulk-submit ALL answers (not just unsubmitted ones) for reliability.
  // This replaces the entire psychometric_answers column atomically.
  const completeTest = useCallback(async () => {
    // Clear all pending debounce timers
    for (const key of Object.keys(debounceTimersRef.current)) {
      clearTimeout(debounceTimersRef.current[key]);
    }
    debounceTimersRef.current = {};

    // Build answer map from local state (source of truth)
    const answerPayload: Record<string, Record<number, number>> = {};
    for (const section of psychometricSections) {
      const sectionAnswers = answers[section.test_slug];
      if (sectionAnswers && Object.keys(sectionAnswers).length > 0) {
        answerPayload[section.test_slug] = sectionAnswers;
      }
    }

    const res = await bulkSubmitPsychometricAnswers(interviewId, answerPayload);
    if (res.error) {
      console.error('[usePsychometricInterview] completeTest bulk submit failed:', res.error);
      toast.error(i18n.t('interview:toasts.psychometric_save_failed'));
      throw new Error(res.error);
    }

    console.log('[usePsychometricInterview] Bulk submitted all answers successfully');
  }, [psychometricSections, answers, interviewId]);

  return {
    currentTestIndex,
    currentQuestionIndex,
    answers,
    currentSection,
    currentQuestion,
    canGoNext,
    canGoPrevious,
    isLastQuestion,
    progress,
    setAnswer,
    currentAnswer,
    goToNext,
    goToPrevious,
    completeTest,
    isComplete,
    unansweredCount,
  };
}
